import { ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import { Flex, FlexColumn } from '../Flex';
import { Button, SecondaryButton } from '..';
import {
  Modal, ModalColorType, ModalProps, ModalSize,
} from './Modal';
import { useStyledTheme } from '../../styling';
import {
  SuccessIcon, InformationIcon, WarningIcon, ErrorIcon,
} from '../Icons';

const ContentWrapper = styled(FlexColumn)`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-weight: ${theme.fontWeight.normal};
  font-size: ${theme.fontSize[2]};
  line-height: ${theme.fontSize[6]};
  gap: ${theme.padding[2]};
`}`;

const ContentHighlight = styled(ContentWrapper)`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
`}`;

// eslint-disable-next-line no-shadow
export enum AlertModalTypeEnum {
  INFORMATION = 'Information',
  WARNING_REVERSIBLE = 'Warning reversible',
  SUCCESS = 'Success',
  WARNING_IRREVERSIBLE = 'Warning irreversible',
  ERROR = 'Error',
  DEFAULT = 'Default',
}

export const AlertModalContent = styled(Flex)`
  gap: ${({ theme }) => theme.padding[4]};

  i {
    font-size: ${({ theme }) => theme.fontSize[7]};
  }
  white-space: pre-wrap;
`;

export type AlertModalType = Partial<ModalProps> & {
  isOpen: boolean,
  title?: string,
  contentHighlight?: string,
  content: string | ReactNode,
  type?: AlertModalTypeEnum,
  size?: ModalSize,
  confirmLabel?: string,
  onConfirm?: () => void,
  cancelLabel?: string,
  onCancel?: () => void,
  onClose?: () => void,
  showButtons?: boolean,
  titleIconOverride?: ReactNode;
  closeOnConfirm?: boolean;
}

export function AlertModal({
  content = '',
  contentHighlight = '',
  title,
  type = AlertModalTypeEnum.DEFAULT,
  size = 'sm',
  isOpen = false,
  onConfirm,
  onCancel,
  onClose,
  confirmLabel,
  cancelLabel,
  showButtons = true,
  titleIconOverride,
  closeOnConfirm = true,
  ...rest
}: AlertModalType) {
  const theme = useStyledTheme();

  const closeModal = useMemo(() => () => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const handleConfirm = useMemo(() => () => {
    if (onConfirm) {
      onConfirm();
    }

    if (closeOnConfirm) {
      closeModal();
    }
  }, [onConfirm, closeModal]);

  const handleCancel = useMemo(() => () => {
    if (onCancel) {
      onCancel();
    }

    closeModal();
  }, [onCancel, closeModal]);

  const contentMessage = useMemo(() => (
    <ContentWrapper>
      {contentHighlight && (
        <ContentHighlight>
          {contentHighlight}
        </ContentHighlight>
      )}
      {content}
    </ContentWrapper>
  ), [content, contentHighlight]);

  const AlertModalColors: { [key: string]: ModalColorType } = useMemo(() => ({
    [AlertModalTypeEnum.ERROR]: {
      headerBackground: theme.colours.danger[1],
      headerTitle: theme.colours.neutral[10],
    },
    [AlertModalTypeEnum.SUCCESS]: {
      headerBackground: theme.colours.success[1],
      headerTitle: theme.colours.neutral[10],
    },
    [AlertModalTypeEnum.INFORMATION]: {
      headerBackground: theme.colours.info[1],
      headerTitle: theme.colours.neutral[10],
    },
    [AlertModalTypeEnum.WARNING_IRREVERSIBLE]: {
      headerBackground: theme.colours.secondary[3],
      headerTitle: theme.colours.primary[1],
    },
    [AlertModalTypeEnum.WARNING_REVERSIBLE]: {
      headerBackground: theme.colours.primary[10],
      headerTitle: theme.colours.primary[1],
    },
    [AlertModalTypeEnum.DEFAULT]: {
      headerBackground: theme.colours.primary[11],
      headerTitle: theme.colours.primary[1],
    },
  }), [theme.colours]);

  const alertModalColor: ModalColorType | undefined = useMemo(
    () => (type ? AlertModalColors[type] : undefined),
    [AlertModalColors, type],
  );

  const alertModalIcon: ReactNode = useMemo(() => {
    const fillColor = alertModalColor?.headerBackground;
    const iconColor = alertModalColor?.headerTitle;
    switch (type) {
      case AlertModalTypeEnum.SUCCESS:
        return <SuccessIcon fill={fillColor} />;
      case AlertModalTypeEnum.INFORMATION:
        return <InformationIcon fill={fillColor} />;
      case AlertModalTypeEnum.ERROR:
        return <ErrorIcon fill={fillColor} />;
      case AlertModalTypeEnum.WARNING_IRREVERSIBLE:
        return <WarningIcon fill={theme.colours.neutral[10]} stroke={iconColor} />;
      case AlertModalTypeEnum.WARNING_REVERSIBLE:
        return <WarningIcon fill={theme.colours.neutral[10]} stroke={iconColor} />;
      default:
        return undefined;
    }
  }, [alertModalColor, type]);

  const footer = useMemo(() => {
    let buttonConfirmLabel;
    let buttonCancelLabel;

    switch (type) {
      case AlertModalTypeEnum.SUCCESS:
      case AlertModalTypeEnum.INFORMATION:
        buttonCancelLabel = 'Cancel';
        break;
      case AlertModalTypeEnum.ERROR:
        buttonConfirmLabel = 'OK';
        break;
      case AlertModalTypeEnum.WARNING_REVERSIBLE:
      case AlertModalTypeEnum.WARNING_IRREVERSIBLE:
        buttonConfirmLabel = 'Delete';
        buttonCancelLabel = 'Cancel';
        break;
      case AlertModalTypeEnum.DEFAULT:
        buttonConfirmLabel = 'OK';
        buttonCancelLabel = 'Cancel';
        break;
      default:
        break;
    }

    // override labels
    if (confirmLabel != null) {
      buttonConfirmLabel = confirmLabel;
    }

    if (cancelLabel != null) {
      buttonCancelLabel = cancelLabel;
    }

    if (!showButtons) return null;

    return (
      <>
        {buttonCancelLabel && <SecondaryButton id="btn-alert-cancel" onClick={handleCancel} small>{buttonCancelLabel}</SecondaryButton>}
        {buttonConfirmLabel && <Button id="btn-alert-confirm" onClick={handleConfirm} small>{buttonConfirmLabel}</Button>}
      </>
    );
  }, [confirmLabel, cancelLabel, handleConfirm, handleCancel]);

  return (
    <Modal
      title={title ?? type ?? ''}
      titleIcon={titleIconOverride ?? alertModalIcon}
      isOpen={isOpen}
      hideModal={() => closeModal()}
      size={size}
      preventOutsideClose
      modalColor={alertModalColor ?? undefined}
      footer={footer}
      {...rest}
    >
      {contentMessage}
    </Modal>
  );
}
